import { isValidCivilDate } from './dates';
import { FIRESTORE_TRANSACTION_TYPES } from './firestoreSchema';
import { getAmountCents } from './money';
import { createRecurringTransactionId } from './recurrenceMaterialization';
import {
  RECURRENCE_INVALID_DAY_POLICIES,
  RECURRENCE_UNITS,
  createOccurrenceKey,
  validateRecurrenceRule,
} from './recurrence';

export const RECURRING_SEGMENT_SCHEMA_VERSION = 1;
export const RECURRING_OCCURRENCE_STATE_SCHEMA_VERSION = 1;

export const RECURRING_SEGMENT_STATUS = Object.freeze({
  ACTIVE: 'active',
  CANCELLED: 'cancelled',
});

export const RECURRING_OCCURRENCE_STATE = Object.freeze({
  MATERIALIZED: 'materialized',
  SKIPPED: 'skipped',
});

export const RECURRENCE_SCHEMA_ERROR_CODES = Object.freeze({
  INVALID_SEGMENT: 'INVALID_RECURRING_SEGMENT',
  INVALID_OCCURRENCE_STATE: 'INVALID_RECURRING_OCCURRENCE_STATE',
});

const SEGMENT_KEYS = [
  'seriesId', 'status', 'template', 'schedule', 'effectiveFromDate',
  'effectiveUntilDate', 'revision', 'createdByUid', 'updatedByUid',
  'createdAt', 'updatedAt', 'schemaVersion', 'installment',
];
const SEGMENT_REQUIRED_KEYS = SEGMENT_KEYS.filter((key) => key !== 'installment');
const SEGMENT_BUILD_KEYS = SEGMENT_KEYS.filter((key) => ![
  'createdAt', 'updatedAt', 'schemaVersion',
].includes(key));
const TEMPLATE_KEYS = ['type', 'amountCents', 'category', 'name', 'attributedToUid', 'attributedToNameSnapshot'];
const TEMPLATE_REQUIRED_KEYS = ['type', 'amountCents', 'category', 'name'];
const SCHEDULE_KEYS = ['unit', 'interval', 'anchorDate', 'untilDate', 'invalidDayPolicy'];
const INSTALLMENT_KEYS = ['currentInstallment', 'totalInstallments', 'totalAmountCents'];
const INSTALLMENT_REQUIRED_KEYS = INSTALLMENT_KEYS.filter((key) => key !== 'totalAmountCents');
const OCCURRENCE_KEYS = [
  'occurrenceKey', 'seriesId', 'segmentId', 'scheduledDate', 'effectiveDate',
  'state', 'transactionId', 'createdByUid', 'updatedByUid',
  'createdAt', 'updatedAt', 'schemaVersion',
];
const OCCURRENCE_REQUIRED_KEYS = OCCURRENCE_KEYS.filter((key) => key !== 'transactionId');
const OCCURRENCE_BUILD_KEYS = OCCURRENCE_KEYS.filter((key) => ![
  'createdAt', 'updatedAt', 'schemaVersion',
].includes(key));
const ID_PATTERN = /^[A-Za-z0-9_-]+$/;

export class RecurrenceSchemaError extends Error {
  constructor(code, errors) {
    super(code);
    this.name = 'RecurrenceSchemaError';
    this.code = code;
    this.errors = errors;
  }
}

const isRecord = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);
const hasOwn = (value, key) => Object.prototype.hasOwnProperty.call(value, key);
const isValidId = (value) => (
  typeof value === 'string' && value.length > 0 && value.length <= 128 && ID_PATTERN.test(value)
);
const isValidTransactionId = (value) => (
  typeof value === 'string' && value.length > 0 && value.length <= 160 && ID_PATTERN.test(value)
);
const isTimestampLike = (value) => (
  value instanceof Date || (isRecord(value) && typeof value.toDate === 'function')
);
const hasExactKeys = (value, allowedKeys, requiredKeys = allowedKeys) => (
  isRecord(value)
  && Object.keys(value).every((key) => allowedKeys.includes(key))
  && requiredKeys.every((key) => hasOwn(value, key))
);
const isValidAmountCents = (value) => getAmountCents({ amountCents: value }) === value;

const validateTemplate = (template, errors) => {
  if (!hasExactKeys(template, TEMPLATE_KEYS, TEMPLATE_REQUIRED_KEYS)) {
    errors.push('template.keys');
    return;
  }
  if (!Object.values(FIRESTORE_TRANSACTION_TYPES).includes(template.type)) errors.push('template.type');
  if (!isValidAmountCents(template.amountCents)) errors.push('template.amountCents');
  if (typeof template.category !== 'string' || template.category.length > 80) errors.push('template.category');
  if (typeof template.name !== 'string' || template.name.length === 0 || template.name.length > 160) {
    errors.push('template.name');
  }
  if (template.attributedToUid !== undefined && !isValidId(template.attributedToUid)) errors.push('template.attributedToUid');
  if (template.attributedToNameSnapshot !== undefined
    && (typeof template.attributedToNameSnapshot !== 'string' || !template.attributedToNameSnapshot.length || template.attributedToNameSnapshot.length > 120)) {
    errors.push('template.attributedToNameSnapshot');
  }
};

const validateSchedule = (schedule, errors) => {
  if (!hasExactKeys(schedule, SCHEDULE_KEYS)) {
    errors.push('schedule.keys');
    return;
  }
  if (!Object.values(RECURRENCE_UNITS).includes(schedule.unit)) errors.push('schedule.unit');
  if (!Number.isSafeInteger(schedule.interval) || schedule.interval < 1) errors.push('schedule.interval');
  if (!isValidCivilDate(schedule.anchorDate)) errors.push('schedule.anchorDate');
  if (schedule.untilDate !== null && !isValidCivilDate(schedule.untilDate)) errors.push('schedule.untilDate');
  if (schedule.invalidDayPolicy !== RECURRENCE_INVALID_DAY_POLICIES.CLAMP) {
    errors.push('schedule.invalidDayPolicy');
  }
};

export const validateRecurringSegmentData = (data) => {
  const errors = [];
  if (!hasExactKeys(data, SEGMENT_KEYS, SEGMENT_REQUIRED_KEYS)) return { valid: false, errors: ['segment.keys'] };

  if (!isValidId(data.seriesId)) errors.push('seriesId');
  if (!Object.values(RECURRING_SEGMENT_STATUS).includes(data.status)) errors.push('status');
  validateTemplate(data.template, errors);
  validateSchedule(data.schedule, errors);
  if (data.installment !== undefined) {
    if (!hasExactKeys(data.installment, INSTALLMENT_KEYS, INSTALLMENT_REQUIRED_KEYS)) errors.push('installment.keys');
    else {
      if (!Number.isSafeInteger(data.installment.currentInstallment) || data.installment.currentInstallment < 1) errors.push('installment.currentInstallment');
      if (!Number.isSafeInteger(data.installment.totalInstallments) || data.installment.totalInstallments < data.installment.currentInstallment) errors.push('installment.totalInstallments');
      if (data.installment.totalAmountCents !== undefined && !isValidAmountCents(data.installment.totalAmountCents)) errors.push('installment.totalAmountCents');
    }
  }
  if (!isValidCivilDate(data.effectiveFromDate)) errors.push('effectiveFromDate');
  if (data.effectiveUntilDate !== null && !isValidCivilDate(data.effectiveUntilDate)) {
    errors.push('effectiveUntilDate');
  }
  if (!Number.isSafeInteger(data.revision) || data.revision < 1) errors.push('revision');
  if (!isValidId(data.createdByUid)) errors.push('createdByUid');
  if (!isValidId(data.updatedByUid)) errors.push('updatedByUid');
  if (!isTimestampLike(data.createdAt)) errors.push('createdAt');
  if (!isTimestampLike(data.updatedAt)) errors.push('updatedAt');
  if (data.schemaVersion !== RECURRING_SEGMENT_SCHEMA_VERSION) errors.push('schemaVersion');

  if (isValidCivilDate(data.schedule?.anchorDate) && isValidCivilDate(data.effectiveFromDate)) {
    if (data.effectiveFromDate < data.schedule.anchorDate) errors.push('effectiveFromDateBeforeAnchorDate');
    if (isValidCivilDate(data.schedule.untilDate) && data.schedule.untilDate < data.effectiveFromDate) {
      errors.push('untilDateBeforeEffectiveFromDate');
    }
  }
  if (
    isValidCivilDate(data.effectiveFromDate)
    && isValidCivilDate(data.effectiveUntilDate)
    && data.effectiveUntilDate < data.effectiveFromDate
  ) errors.push('effectiveRange');

  const domainValidation = validateRecurrenceRule({
    seriesId: data.seriesId,
    unit: data.schedule?.unit,
    interval: data.schedule?.interval,
    anchorDate: data.schedule?.anchorDate,
    untilDate: data.schedule?.untilDate,
    effectiveFromDate: data.effectiveFromDate,
    effectiveUntilDate: data.effectiveUntilDate,
    invalidDayPolicy: data.schedule?.invalidDayPolicy,
  });
  if (!domainValidation.valid) errors.push(...domainValidation.errors.map((error) => `domain.${error}`));
  return { valid: errors.length === 0, errors: [...new Set(errors)] };
};

const fakeTimestamp = { toDate: () => new Date(0) };

export const buildRecurringSegmentPayload = (input) => {
  const requiredBuildKeys = SEGMENT_BUILD_KEYS.filter((key) => key !== 'installment');
  if (!hasExactKeys(input, SEGMENT_BUILD_KEYS, requiredBuildKeys)) {
    throw new RecurrenceSchemaError(RECURRENCE_SCHEMA_ERROR_CODES.INVALID_SEGMENT, ['segment.keys']);
  }
  const payload = { ...input, schemaVersion: RECURRING_SEGMENT_SCHEMA_VERSION };
  const validation = validateRecurringSegmentData({
    ...payload,
    createdAt: fakeTimestamp,
    updatedAt: fakeTimestamp,
  });
  if (!validation.valid) {
    throw new RecurrenceSchemaError(RECURRENCE_SCHEMA_ERROR_CODES.INVALID_SEGMENT, validation.errors);
  }
  return payload;
};

export const fromFirestoreRecurringSegment = (id, data) => {
  const validation = validateRecurringSegmentData(data);
  if (!isValidId(id) || !validation.valid) {
    throw new RecurrenceSchemaError(
      RECURRENCE_SCHEMA_ERROR_CODES.INVALID_SEGMENT,
      !isValidId(id) ? ['segmentId', ...validation.errors] : validation.errors
    );
  }
  return { id, ...data };
};

export const toDomainRecurrenceRule = (segment) => ({
  seriesId: segment.seriesId,
  unit: segment.schedule.unit,
  interval: segment.schedule.interval,
  anchorDate: segment.schedule.anchorDate,
  untilDate: segment.schedule.untilDate,
  effectiveFromDate: segment.effectiveFromDate,
  effectiveUntilDate: segment.effectiveUntilDate,
  invalidDayPolicy: segment.schedule.invalidDayPolicy,
});

const expectedOccurrenceKey = (seriesId, scheduledDate) => {
  try {
    return createOccurrenceKey(seriesId, scheduledDate);
  } catch (_) {
    return null;
  }
};

export const validateRecurringOccurrenceStateData = (data, documentId = null) => {
  const errors = [];
  if (!hasExactKeys(data, OCCURRENCE_KEYS, OCCURRENCE_REQUIRED_KEYS)) {
    return { valid: false, errors: ['occurrence.keys'] };
  }
  if (!isValidId(data.seriesId)) errors.push('seriesId');
  if (!isValidId(data.segmentId)) errors.push('segmentId');
  if (!isValidCivilDate(data.scheduledDate)) errors.push('scheduledDate');
  if (!isValidCivilDate(data.effectiveDate)) errors.push('effectiveDate');
  if (!Object.values(RECURRING_OCCURRENCE_STATE).includes(data.state)) errors.push('state');
  if (data.occurrenceKey !== expectedOccurrenceKey(data.seriesId, data.scheduledDate)) errors.push('occurrenceKey');
  if (documentId !== null && data.occurrenceKey !== documentId) errors.push('documentId');
  if (data.state === RECURRING_OCCURRENCE_STATE.MATERIALIZED) {
    if (!isValidTransactionId(data.transactionId)) errors.push('transactionId');
    else {
      try {
        if (data.transactionId !== createRecurringTransactionId(data.seriesId, data.scheduledDate)) {
          errors.push('transactionIdIdentity');
        }
      } catch (_) {
        errors.push('transactionIdIdentity');
      }
    }
  } else {
    if (hasOwn(data, 'transactionId')) errors.push('transactionId');
    if (data.effectiveDate !== data.scheduledDate) errors.push('effectiveDateForSkipped');
  }
  if (!isValidId(data.createdByUid)) errors.push('createdByUid');
  if (!isValidId(data.updatedByUid)) errors.push('updatedByUid');
  if (!isTimestampLike(data.createdAt)) errors.push('createdAt');
  if (!isTimestampLike(data.updatedAt)) errors.push('updatedAt');
  if (data.schemaVersion !== RECURRING_OCCURRENCE_STATE_SCHEMA_VERSION) errors.push('schemaVersion');
  return { valid: errors.length === 0, errors };
};

export const buildRecurringOccurrenceStatePayload = (input) => {
  const requiredBuildKeys = OCCURRENCE_BUILD_KEYS.filter((key) => key !== 'transactionId');
  if (!hasExactKeys(input, OCCURRENCE_BUILD_KEYS, requiredBuildKeys)) {
    throw new RecurrenceSchemaError(RECURRENCE_SCHEMA_ERROR_CODES.INVALID_OCCURRENCE_STATE, ['occurrence.keys']);
  }
  const payload = { ...input, schemaVersion: RECURRING_OCCURRENCE_STATE_SCHEMA_VERSION };
  const validation = validateRecurringOccurrenceStateData({
    ...payload,
    createdAt: fakeTimestamp,
    updatedAt: fakeTimestamp,
  }, input.occurrenceKey);
  if (!validation.valid) {
    throw new RecurrenceSchemaError(
      RECURRENCE_SCHEMA_ERROR_CODES.INVALID_OCCURRENCE_STATE,
      validation.errors
    );
  }
  return payload;
};

export const fromFirestoreRecurringOccurrenceState = (id, data) => {
  const validation = validateRecurringOccurrenceStateData(data, id);
  if (!validation.valid) {
    throw new RecurrenceSchemaError(
      RECURRENCE_SCHEMA_ERROR_CODES.INVALID_OCCURRENCE_STATE,
      validation.errors
    );
  }
  return { id, ...data };
};
